import React, {useEffect, useState} from 'react';
import {Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, MenuItem, Paper, TextField, Typography,} from '@mui/material';
import {Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, EmojiEvents as BadgeIcon} from '@mui/icons-material';
import badgeService from '../../services/badgeService';

const MemberBadgeSection = ({memberId, initialBadges = null, viewMode = false}) => {
    const [badges, setBadges] = useState([]); // Alle verfügbaren Badges
    const [memberBadges, setMemberBadges] = useState([]); // Badges des Mitglieds
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

    // Dialog States
    const [openDialog, setOpenDialog] = useState(false);
    const [editingBadge, setEditingBadge] = useState(null);
    const [formData, setFormData] = useState({
        badgeId: '',
        awardedAt: new Date().toISOString().split('T')[0],
        awardedIn: '',
    });

    useEffect(() => {
        if (memberId) {
            // Nur beim ersten Laden initialBadges verwenden
            if (!hasLoadedInitial && initialBadges !== null && initialBadges !== undefined && Array.isArray(initialBadges)) {
                setMemberBadges(initialBadges);
                setHasLoadedInitial(true);
            } else {
                // Nach dem ersten Laden oder wenn keine initialBadges: immer von API laden
                loadMemberBadges();
            }
            loadAllBadges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberId]);

    const loadAllBadges = async () => {
        try {
            const data = await badgeService.getAllBadges();
            setBadges(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[loadAllBadges] Fehler beim Laden der Badges:', err);
            setBadges([]); // Setze leeres Array bei Fehler
        }
    };

    const loadMemberBadges = async () => {
        try {
            setLoading(true);
            const data = await badgeService.getMemberBadges(memberId);
            setMemberBadges(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[loadMemberBadges] Fehler beim Laden der Mitglieder-Badges:', err);
            setError('Fehler beim Laden der Abzeichen');
            setMemberBadges([]); // Setze leeres Array bei Fehler
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (memberBadge = null) => {
        if (memberBadge) {
            setEditingBadge(memberBadge);
            setFormData({
                badgeId: memberBadge.badge.id,
                awardedAt: memberBadge.awardedAt ? new Date(memberBadge.awardedAt).toISOString().split('T')[0] : '',
                awardedIn: memberBadge.awardedIn || '',
            });
        } else {
            setEditingBadge(null);
            setFormData({
                badgeId: '',
                awardedAt: new Date().toISOString().split('T')[0],
                awardedIn: '',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingBadge(null);
        setFormData({
            badgeId: '',
            awardedAt: new Date().toISOString().split('T')[0],
            awardedIn: '',
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const submitData = {
                badgeId: parseInt(formData.badgeId),
                awardedAt: new Date(formData.awardedAt).toISOString(),
                awardedIn: formData.awardedIn || null,
            };

            if (editingBadge) {
                await badgeService.updateMemberBadge(memberId, editingBadge.id, submitData);
                setSuccess('Abzeichen erfolgreich aktualisiert!');
            } else {
                await badgeService.awardBadge(memberId, submitData);
                setSuccess('Abzeichen erfolgreich verliehen!');
            }

            handleCloseDialog();
            loadMemberBadges();
        } catch (err) {
            console.error('[handleSubmit] Fehler:', err);
            setError(err.response?.data?.message || 'Fehler beim Speichern des Abzeichens');
        }
    };

    const handleDelete = async (memberBadgeId) => {
        if (!window.confirm('Möchten Sie dieses Abzeichen wirklich entfernen?')) {
            return;
        }

        try {
            await badgeService.removeMemberBadge(memberId, memberBadgeId);
            setSuccess('Abzeichen erfolgreich entfernt!');
            // Lade die Badges neu, unabhängig davon ob sie initial übergeben wurden
            loadMemberBadges();
        } catch (err) {
            setError(err.response?.data?.message || 'Fehler beim Entfernen des Abzeichens');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('de-DE');
    };

    if (!memberId) {
        return null;
    }

    return (
        <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                <Typography variant="subtitle2" color="primary" sx={{fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1}}>
                    <BadgeIcon/>
                    Abzeichen
                </Typography>
                {!viewMode && (
                    <Button
                        size="small"
                        startIcon={<AddIcon/>}
                        onClick={() => handleOpenDialog()}
                        variant="outlined"
                    >
                        Abzeichen verleihen
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 2}} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <Typography variant="body2" color="textSecondary">
                    Lade Abzeichen...
                </Typography>
            ) : memberBadges.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{py: 2, textAlign: 'center'}}>
                    Noch keine Abzeichen verliehen
                </Typography>
            ) : (
                <List sx={{p: 0}}>
                    {memberBadges.map((memberBadge) => (
                        <Paper key={memberBadge.id} sx={{mb: 1, p: 2}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                <Box sx={{flex: 1}}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {memberBadge.badge.name}
                                        </Typography>
                                        {memberBadge.badge.requiredAge && (
                                            <Chip label={`ab ${memberBadge.badge.requiredAge} Jahren`} size="small" color="primary"/>
                                        )}
                                    </Box>
                                    {memberBadge.badge.description && (
                                        <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                                            {memberBadge.badge.description}
                                        </Typography>
                                    )}
                                    <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>Verliehen am:</strong> {formatDate(memberBadge.awardedAt)}
                                        </Typography>
                                        {memberBadge.awardedIn && (
                                            <Typography variant="caption" color="textSecondary">
                                                <strong>In:</strong> {memberBadge.awardedIn}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {!viewMode && (
                                    <Box>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(memberBadge)}
                                            title="Bearbeiten"
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(memberBadge.id)}
                                            title="Entfernen"
                                        >
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    ))}
                </List>
            )}

            {/* Badge hinzufügen/bearbeiten Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingBadge ? 'Abzeichen bearbeiten' : 'Abzeichen verleihen'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}>
                            <TextField
                                select
                                label="Abzeichen"
                                value={formData.badgeId}
                                onChange={(e) => setFormData({...formData, badgeId: e.target.value})}
                                required
                                fullWidth
                            >
                                <MenuItem value="">Bitte wählen</MenuItem>
                                {badges.map((badge) => (
                                    <MenuItem key={badge.id} value={badge.id}>
                                        {badge.name}
                                        {badge.requiredAge && ` (ab ${badge.requiredAge} Jahren)`}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="Verliehen am"
                                type="date"
                                value={formData.awardedAt}
                                onChange={(e) => setFormData({...formData, awardedAt: e.target.value})}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Verliehen in (Ort)"
                                value={formData.awardedIn}
                                onChange={(e) => setFormData({...formData, awardedIn: e.target.value})}
                                fullWidth
                                placeholder="z.B. Feuerwehrhaus Musterstadt"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Abbrechen</Button>
                        <Button type="submit" variant="contained">
                            {editingBadge ? 'Aktualisieren' : 'Verleihen'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default MemberBadgeSection;

